import React, { Component } from "react";
import { connect } from "react-redux";
import { startCase, get } from "lodash";
import { interpolatePuOr, rgb } from "d3";
import scrollIntoView from "scroll-into-view-if-needed";
import { instanceOf } from "prop-types";
import { Cookies, withCookies } from "react-cookie";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Moment from "moment";
import _ from "lodash";
import { getQueryParams } from "../../utils/uriParams";

import BaseMap, { setBoundsToSources } from "../maps/BaseMap";
import * as olStyles from "../maps/olStyles";
import * as Scroll from "react-scroll";
import {
  defaultPolyStyle,
  defaultSymbolStyle,
  gradientLineStyle,
  groupedPointStyle,
  onlyHoverPointStyle,
} from "../maps/BaseMapStyles";

import { getEventColorStyle } from "../../utils/eventColors";

import dataFilters from "../maps/dataFilters";
//actions and calls
import {
  pullCanvasserStatuses,
  pullCanvasserEvents,
  pullCanvasserBreadcrumbs,
  pullAssignedTurfs,
  pullAssignedSites,
  pullCampaignOffices,
  updateCampaignAggregates,
} from "../../store/api/calls";
import api from "../../store/api/api";
import {
  addMapLayer,
  removeMapLayer,
  clearMapLayers,
  updateMapLayer,
} from "../../store/actions";

//components
import StarMarker from "../maps/markers/StarMarker";
import CanvasserListItem from "../maps/listItems/CanvasserListItem";
import CanvasserLogListItem from "../maps/listItems/CanvasserLogListItem";
import CanvasserActionListItem from "../maps/listItems/CanvasserActionListItem";
import OfficeListItem from "../maps/listItems/OfficeListItem";
import SiteListItem from "../maps/listItems/SiteListItem";
import NoDataListItem from "../maps/listItems/NoDataListItem";
import TurfListItem from "../maps/listItems/TurfListItem";
import FloatHeader from "../theme/headers/FloatHeader";
import { coloredPolygonStyle } from "../maps/olStyles";
import { genericSquareStyle } from "../maps/olStyles";
import { defaultLineStringStyle } from "../maps/olStyles";
import { genericCircleStyle } from "../maps/olStyles";
import ImageViewBox from "../imgs/imageViewBox";
import CanvasserEventsTimeline from "../theme/charts/canvasserEventsTimeline";
import FeedbackTile from "../theme/modals/FeedbackTile";
import { defaultPointStyle } from "../maps/BaseMapStyles";
import convex from "@turf/convex";
import transformScale from "@turf/transform-scale";

const PERCENTAGE_INCREMENT = 5;
const generateId = (id) => `list-component-${id}`;

class TrackerMap extends Component {
  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };

  constructor(props) {
    super(props);

    this.markerColorArray = [
      "#a6cee3",
      "#1f78b4",
      "#b2df8a",
      "#33a02c",
      "#fb9a99",
      "#e31a1c",
      "#fdbf6f",
      "#ff7f00",
      "#cab2d6",
      "#6a3d9a",
      "#ffff99",
      "#b15928",
    ];

    this.pullTimeout = null;

    const { cookies } = props;
    this.state = {
      //mapstuff
      loaded: false,
      focusedMarker: null,
      center: {
        lat: 39.8333333,
        lng: -98.585522,
      },
      zoom: 4,
      //sidebar
      sideBarClosed: false,
      noGeoCanvassers: [],

      //key display variables
      sideBarLayerFocused: null, //which layer in the cluster is actually listed in the sidebar
      focusedCluster: this.getClusterId("campaign", props.focusedCampaign.id),
      date: new Moment().format("YYYY-MM-DD"),

      refresh: 0,
      //data filters
      appliedFilters: [], // currently out of use. You can default apply filters to data by adding their text name here. Filters live in ../maps/DataFilters.
      animationRate: PERCENTAGE_INCREMENT,

      scrubberPercentage: 100,
      scrubName: null,
      siteColorMode: "siteroute", //routeseat or siteroute
    };

    this.SUBMIT_DATE_FORMAT = "YYYY-MM-DD";

    this.mapRef = React.createRef();

    //a variable we use to store the loaded data when we load direct into a canvasser view. WE need this to get the office id and other info,
    // and to correctly load layers higher up the parent chain.
    this.canvasserDataOnLoad = {};

    //the base organization:
    // which types of layers we should expect in each cluster,
    // and the various properties of the cluster types
    this.config = {
      clusterTypes: {
        //Note that the clusters have more layers than just the layers we load.
        //Statuses, for instance, default loads at the campaign level and creates a layer for each campaign,
        // office, and canvasser at load. The layers to load is a tool to figure out what we need to laod.
        //The layers that should be displayed is in the expectedLayers key.

        campaign: {
          layersToLoad: ["offices", "statuses"],
          expectedLayers: ["offices", "statuses"],
          getParentId: () => null,
          defaultDisplayedLayerType: "offices",
          assignColorFunc: this.assignColorsToCampaignLayers,
          getClusterName: (cluster_id) => this.props.focusedCampaign.name,
        },
        office: {
          layersToLoad: ["statuses", "turfs", "routesites"],
          expectedLayers: ["statuses", "turfs", "routesites"],
          getParentId: () =>
            this.getClusterId("campaign", this.props.focusedCampaign.id),
          defaultDisplayedLayerType: "statuses",
          assignColorFunc: this.assignColorsToOfficeLayers,
          getClusterName: (cluster_id) => {
            return (
              this.props.mapLayers.find(
                (layer) =>
                  layer.type === "statuses" && layer.cluster.id === cluster_id
              ) || { cluster: {} }
            ).cluster.name;
          },
        },
        canvasser: {
          layersToLoad: ["breadcrumbs", "events"],
          expectedLayers: [
            "breadcrumbs",
            "events",
            "statuses",
            "turfs",
            "routesites",
          ],
          sideBarDisplayedLayers: [
            "breadcrumbs",
            "events",
            "turfs",
            "routesites",
          ],
          getParentId: (cluster_id) =>
            this.getCanvasserParentClusterId(cluster_id),
          defaultDisplayedLayerType: "events", //change to events
          assignColorFunc: this.assignColorsToCanvasserLayers,
          getClusterName: (cluster_id) => {
            return (
              this.props.mapLayers.find(
                (layer) =>
                  layer.type === "statuses" && layer.cluster.id === cluster_id
              ) || { cluster: {} }
            ).cluster.name;
          },
        },
      },

      //every layer needs a setColor, setPullLoop, func that takes the layer_id (and hence knows the cluster);
      layerTypes: {
        offices: {
          pullLoop: () => {},
          pullFrequency: 0,
          pullFunc: this.pullOffices,
          listComponent: OfficeListItem,
          mapComponent: StarMarker,
          srcConfigFunc: this.getOfficeSrcConfig,
          colorFunc: this.getOfficeColor,
          styleFunc: this.getOfficeSrcConfig,
          encodingFunc: this.encodeOfficeData,
          sideBarDisplayFunc: this.buildOfficeSideBar, //alwasy takes the layer you are building
          excludeFromFit: true,
        },

        statuses: {
          pullLoop: () => {},
          pullFrequency: 3 * 60 * 1000,
          pullFunc: this.pullCanvasserStatuses,
          listComponent: CanvasserListItem,
          displayName: "Canvassers",
          styleFunc: this.assignStatusStyle,
          colorFunc: this.getStatusColor,
          sideBarDisplayFunc: this.buildStatusSideBar,
          srcConfigFunc: this.getStatusSrcConfig,
          encodingFunc: this.encodeStatusData,
        },

        turfs: {
          pullLoop: () => {},
          pullFrequency: 90,
          pullFunc: this.pullAssignedTurfs,
          listComponent: TurfListItem,
          styleFunc: this.assignTurfStyle,
          colorFunc: this.getTurfColor,
          sideBarDisplayFunc: this.buildTurfSideBar,
          srcConfigFunc: this.getTurfSrcConf,
          encodingFunc: this.encodeTurfData,
        },

        routesites: {
          pullLoop: () => {},
          pullFrequency: 0,
          pullFunc: this.pullAssignedSites,
          listComponent: SiteListItem,
          styleFunc: this.assignSiteStyle,
          colorFunc: this.getSiteColor,
          sideBarDisplayFunc: this.buildSiteSideBar,
          srcConfigFunc: this.getRouteSiteSrcConf,
          encodingFunc: this.encodeRouteSiteData,
        },

        breadcrumbs: {
          pullLoop: () => {},
          pullFrequency: 0, //3 * 60 * 1000,
          pullFunc: this.pullCanvasserBreadcrumbs,
          listComponent: CanvasserLogListItem,
          styleFunc: this.assignBreadcrumbPointStyle, // polygons rendered in the group by Function
          colorFunc: this.getBreadcrumbColor,
          sideBarDisplayFunc: this.buildBreadcrumbSideBar,
          srcConfigFunc: this.getBreadcrumbSrcConf,
          encodingFunc: this.encodeBreadcrumbData,
        },

        events: {
          pullLoop: () => {},
          pullFrequency: 0, // 60 * 1000,
          pullFunc: this.pullCanvasserEvents,
          listComponent: CanvasserActionListItem,
          styleFunc: this.assignEventStyle,
          colorFunc: this.getEventColor,
          sideBarDisplayFunc: this.buildEventSideBar,
          srcConfigFunc: this.getEventSrcConf,
          encodingFunc: this.encodeEventData,
        },
      },
      // these are the the event counts
      campaignAggregates: {
        pullLoop: () => {},
        pullFrequency: 60 * 3 * 1000,
        pullFunc: this._updateCampaignAggregates,
        updated: Infinity,
      },
    };
  }

  componentDidMount() {
    this.props.clearMapLayers();

    //if it's a canvasser, we first need to get the office -- Otherwise we can properly set the cluster chain to load.
    const canvasser_id = getQueryParams(this.props.location).canvasser;
    let promise;

    if (canvasser_id) {
      promise = () =>
        api
          .get(`/canvassers/${canvasser_id}`)
          .then((res) => (this.canvasserDataOnLoad = res.data));
    } else {
      promise = pullCampaignOffices;
    }

    promise()
      .then(this.matchLayersAndDataToUri) //returns a promise when state update is complete, so we know te correct vars are in state;
      .then(() => {
        //load aggregates and set a loop
        let aggConfig = this.config.campaignAggregates;
        //pull now if not fresh;
        if (Date.now() - aggConfig.updated < aggConfig.pullFrequency) {
          this._updateCampaignAggregates();
        }

        clearTimeout(this.config.campaignAggregates.pullLoop);
        //set a loop
        const pullAggregatesOnLoop = () => {
          this.config.campaignAggregates.pullLoop = setTimeout(() => {
            this._updateCampaignAggregates();
            this.config.campaignAggregates.updated = Date.now();
            pullAggregatesOnLoop();
          }, this.config.campaignAggregates.pullFrequency);
        };

        pullAggregatesOnLoop();
      });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    //used to reset bounds as an after effect of state changes.
    //DO NOT HANDLE DATA LOAD POST PROCESSING HERE
    if (this.props.mapLayers.length && !prevProps.mapLayers.length) {
      this.setState({ loaded: true });
    }

    //set bounds on layer change
    if (this.state.sideBarLayerFocused !== prevState.sideBarLayerFocused) {
      console.log("HERE");
      this.setBounds();
    }
  }

  componentWillUnmount() {
    //clear the many pull loops we set.
    //layers
    Object.keys(this.config.layerTypes).forEach((k) => {
      clearTimeout(this.config.layerTypes[k].pullLoop);
    });
    //aggs
    clearTimeout(this.config.campaignAggregates.pullLoop);
  }

  // needed to set bounds
  getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height,
    };
  }

  /*
   *  THIS SECTION ENCOMPASES THE MAJOR PIECES OF THE THE STATE UPDATE CYCLE THAT ARE NOT A DATA PULL
   *
   * OVERVIEW OF LIFECYCLE:
   * THE MAP LOAD AND DISPLAY CYCLE IS PRINCIPALLY DEPENDENT ON TWO VARIABLES: DATE AND FOCUSED CLUSTER (THE ID OF THE DISPLAYED CLUSTER)
   * PROCESS TO UPDATE EITHER OF THESE VARIABLES
   *
   * 1) UPDATE STATE
   * 2) MATCH THE URI TO STATE TO SO A USER REFRESH GENERATES EXACTLY THE SAME DATA
   * 3) CHECK EXISTING DATA AGAINST THE LAYERS WE WOULD EXPECT TO HAVE IN THE DATA MAP DICT
   * 4) IF WE HAVE THE DATA, CHECK THAT THE DATA THAT EXISTS IS FRESH ENOUGH
   * 5) IF IT ISN'T, PULL IMMEDIATELY,
   * 6) RESET THE FUNCTIONS IN THE LAYERTYPE TIMEOUT LOOP SO THEY ARE PULLING DATA THAT IS FILTERED TO THE CURRENT DISPLAYED CLUSTER
   *
   * */

  updateCluster = (cluster_id) => {
    let { type, instance_id } = this.splitClusterId(cluster_id);
    let sideBarLayerFocused = this.getLayerId(
      type,
      instance_id,
      this.config.clusterTypes[type].defaultDisplayedLayerType
    );
    this.setState(
      { focusedCluster: cluster_id, sideBarLayerFocused },
      this.matchDataLayersToFocusedCluster
    ); //after update, initial cache checks, reset pull loops, and get correct data
    this.setClusterAndDateInURIParams(cluster_id);
  };

  updateDate = (jsDateObj) => {
    let date = Moment(jsDateObj).format(this.SUBMIT_DATE_FORMAT);
    if (date !== this.state.date) {
      this.props.clearMapLayers();
      this.setState({ date }, () => {
        this.matchDataLayersToFocusedCluster().then(() =>
          this._updateCampaignAggregates()
        );
        this.setClusterAndDateInURIParams(null, date);
      });
    }
  };

  matchLayersAndDataToUri = () => {
    //This is called only in componentDidMount -- it is only place we pull state from URI.
    // The rest of the app sets URI after setting state.

    const query = getQueryParams(this.props.location);

    //laid our explicitly here because these are the only keys we look for.
    const campaign = query.campaign || this.props.focusedCampaign.id || 1;
    const office = query.office || null;
    const canvasser = query.canvasser || null;
    const date =
      query.date && /\d{4}-\d{2}-\d{2}/.test(query.date)
        ? query.date
        : new Moment().format("YYYY-MM-DD");
    const archive = !(date === new Moment().format("YYYY-MM-DD")); // used for showing date picker or not;

    //we're going to match the cluster to the highest possible level
    let stateUpdate = {};

    if (canvasser) {
      stateUpdate = {
        focusedCluster: this.getClusterId("canvasser", canvasser),
        sideBarLayerFocused: this.getLayerId(
          "canvasser",
          canvasser,
          this.config.clusterTypes.canvasser.defaultDisplayedLayerType
        ),
      };
    } else if (office) {
      stateUpdate = {
        focusedCluster: this.getClusterId("office", office),
        sideBarLayerFocused: this.getLayerId(
          "office",
          office,
          this.config.clusterTypes.office.defaultDisplayedLayerType
        ),
      };
    } else if (campaign) {
      stateUpdate = {
        focusedCluster: this.getClusterId("campaign", campaign),
        sideBarLayerFocused: this.getLayerId(
          "campaign",
          campaign,
          this.config.clusterTypes.campaign.defaultDisplayedLayerType
        ),
      };
    }

    return new Promise((resolve, reject) => {
      this.setState({ ...stateUpdate, date, archive }, () => {
        this.matchDataLayersToFocusedCluster();
        resolve();
      });
    });
  };

  setClusterAndDateInURIParams = (cluster_id, date) => {
    if (!cluster_id) cluster_id = this.state.focusedCluster;
    if (!date) date = this.state.date;

    let clusterInfo = this.splitClusterId(cluster_id);
    this.props.history.push({
      search: `?${clusterInfo.type}=${clusterInfo.instance_id}&date=${date}`,
    });
  };

  setBounds() {
    const layerToBound = this.props.mapLayers.find(
      (layer) => layer.id === this.state.sideBarLayerFocused
    );

    console.log("LAYER", layerToBound, this.state.sideBarLayerFocused);

    if (
      !layerToBound ||
      !Array.isArray(layerToBound.displayedData) ||
      !layerToBound.displayedData.length > 2 ||
      !this.mapRef.current
    )
      return;
    setBoundsToSources(this.getSources([layerToBound]), this.mapRef);

    // if (
    //   !layerToBound ||
    //   !Array.isArray(layerToBound.displayedData) ||
    //   !layerToBound.displayedData.length
    // )
    //   return;

    // let lats = layerToBound.displayedData
    //   .map((d) => (d.latitude ? d.latitude : d.lat))
    //   .filter((d) => ['', 0, '0', null, NaN].indexOf(+d) < 0);
    // let longs = layerToBound.displayedData
    //   .map((d) => (d.longitude ? d.longitude : d.lon))
    //   .filter((d) => ['', 0, '0', null, NaN].indexOf(+d) < 0);

    // //handle the case of a single point, which has no meaningful bounds
    // if (lats.length === 1 && longs.length === 1) {
    //   this.setState({
    //     center: { lat: lats[0], lng: longs[0] },
    //     zoom: 13,
    //   });
    //   return;
    // }

    // let bounds = {
    //   ne: { lat: Math.max(...lats), lng: Math.max(...longs) },
    //   sw: { lat: Math.min(...lats), lng: Math.min(...longs) },
    // };

    // const { center, zoom } = fitBounds(bounds, this.getWindowDimensions());

    // this.setState({ center, zoom });
  }

  /*
   * PARENT FUNCTIONS. Only currently needed for canvasser, since campaign has no parent and office is always campaign
   * */

  //navigates to the parent of a cluster
  _goToCurrClusterParent = () => {
    let { type } = this.splitClusterId(this.state.focusedCluster);
    if (type === "campaign") return;
    if (!type) type = "office";
    let parent = this.config.clusterTypes[type].getParentId(
      this.state.focusedCluster
    );
    this.updateCluster(parent);
  };

  getCanvasserParentClusterId = (cluster_id) => {
    let cluster_data = this.splitClusterId(cluster_id);
    let parentClusterId;

    //did we pull this data on load?
    if (
      this.canvasserDataOnLoad.id &&
      this.canvasserDataOnLoad.id === +cluster_data.instance_id
    ) {
      parentClusterId = this.getClusterId(
        "office",
        this.canvasserDataOnLoad.office
      );
      //check against the canvasser status layers to find the office id
    } else {
      let canvasserStatusLayer =
        this.props.mapLayers.find(
          (layer) =>
            layer.cluster.id === cluster_id && layer.type === "statuses"
        ) || {};

      if (
        canvasserStatusLayer.displayedData &&
        canvasserStatusLayer.displayedData.length
      ) {
        parentClusterId = (canvasserStatusLayer.cluster || {}).parent;
      }
    }

    if (parentClusterId) {
      return parentClusterId;
    } else {
      //we didn't find what we expected. Fall back to campaign
      //THIS IS IS THE SOURCE OF THE PROBLEM
      return this.getClusterId("campaign", this.props.focusedCampaign.id);
    }
  };

  getAssignedDoorStatus(assigned_door, events) {
    let foundevents = events.filter(
      (d) => +assigned_door.id === +(d.door || {}).id
    );
    if (!foundevents.length) {
      return assigned_door;
    } else {
      return {
        ...assigned_door,
        event_ids: foundevents.map((d) => d.id),
        status: foundevents.map((d) => d.status).includes("canvass")
          ? "canvass"
          : (foundevents.find((d) => d.status) || {}).status,
      };
    }
  }

  /*
   * START DATA PULL SECTION.
   *
   * Functions in this section pull data and
   * handle the logic of setting pull loops
   * and putting data into our map layers system
   * */

  /*
   * MAP LAYERS EXPLANATION
   *
   * The map is basically dividing into sets of layers, which are themselves grouped into clusters
   * These are stored in redux. See our connect function at the bottom.
   *
   * Each Layer must have the following things to be displayed:
   * data: basically a backup copy of the data so we can rerender at any time
   * displayedData: The sorted, filtered thing we actually show
   * visible: true/false, should we display this layer (only checked if the layer is in the focused cluster)
   * mapMarker: string. We point it to a component in the "mapStringToComponent" function. The component that is rendered on the map
   * listItem: string. We point it to a component in the "mapStringToComponent" function. The component that is rendered in the side list.
   *
   *
   * type: eg "turfs", or "routesites"
   * id: type-id-datatype eg "canvasser-12-turfs", "campaign-36-offices", "office-22-routesites",
   * name: what do we call this thing, displayed after passed through _.startCase() function
   * instance_id: the id of the canvasser, office, or campaign the layer references.
   *
   * //clusters are sets of layers that are displayed on the map at the same time. E.g. "events" and "assigned sites" for a canvasser
   * cluster: {
   *   type: must be "canvasser", "office", or "campaign",
   *   id: type-id, eg "canvasser-12", "office-227",
   *   name: what do we call this thing, displayed after passed through _.startCase() function. Do not need to be unique.
   *   parent: where to go when we want to go backwards.
   * }
   *
   * */

  getClusterId = (clusterType, id) => `${clusterType}-${id}`;
  getLayerId = (clusterType, id, layerType) =>
    `${clusterType}-${id}-${layerType}`;

  splitClusterId = (id) => {
    if (!id) return {};

    let split_token = id.split("-");

    if (split_token.length !== 2) return {};

    return {
      type: split_token[0],
      instance_id: split_token[1],
    };
  };

  splitLayerId = (id) => {
    if (!id) return {};

    let split_token = id.split("-");

    if (split_token.length !== 3) return {};

    return {
      clusterType: split_token[0],
      instance_id: split_token[1],
      layerType: split_token[2],
    };
  };

  /*
   * This is the meat of the logic of how we pull and store data.
   *  We check caches, determine what we need, and set timeoutLoops to auto update data.
   * */
  matchDataLayersToFocusedCluster = () => {
    if (!this.state.focusedCluster) {
      console.warn("Load misfire, focusedCluster variable not set in state");
      return;
    }

    const { type } = this.splitClusterId(this.state.focusedCluster);
    let promiseChain = [];

    // we load the level you're looking for and the level above it.
    // They all start with the current cluster. Campaign has no parent, so we just load current cluster in that case.
    let clusterChain = [this.state.focusedCluster];
    if (type === "canvasser") {
      clusterChain.push(
        this.getCanvasserParentClusterId(this.state.focusedCluster)
      );
    } else if (type === "office") {
      clusterChain.push(
        this.getClusterId("campaign", this.props.focusedCampaign.id)
      );
    }

    let parentChain = clusterChain.reverse(); //load from parent down

    const isLayerDataFresh = (layer) => {
      let pullFreq = this.config.layerTypes[layer.type].pullFrequency;
      if (pullFreq < 999) return false; // sub second pull frequencies are never looped and are always pulled on load/
      return Date.now() - layer.updated > pullFreq;
    };

    const setLoopingTimeout = (layerType, instance_id) => {
      clearTimeout(this.config.layerTypes[layerType].pullLoop);

      let { pullFrequency } = this.config.layerTypes[layerType];

      const pullOnTimeout = () => {
        this.config.layerTypes[layerType].pullLoop = setTimeout(() => {
          this.config.layerTypes[layerType].pullFunc(instance_id);
          pullOnTimeout();
        }, pullFrequency);
      };

      if (pullFrequency > 999) pullOnTimeout(); //don't accept timeouts of less than a second.
    };

    parentChain.forEach((cluster_id) => {
      if (!cluster_id) return;

      let { type: clusterType, instance_id: clusterInstanceId } =
        this.splitClusterId(cluster_id);

      let existingAndFreshLayers = this.props.mapLayers
        .filter((layer) => layer.cluster.id === cluster_id)
        .filter(isLayerDataFresh)
        .map((layer) => layer.id);

      let neededLayers = this.config.clusterTypes[clusterType].layersToLoad.map(
        (layerType) => ({
          id: this.getLayerId(clusterType, clusterInstanceId, layerType),
          type: layerType,
          instance_id: clusterInstanceId,
        })
      );

      let layersToLoad = neededLayers.filter(
        (d) => !existingAndFreshLayers.includes(d.id)
      );

      //office special case -- only load statuses for office, so
      if (type === "office" && clusterType === "campaign") {
        //if focused cluster is an office and cluster for which we are loading data is campaign
        layersToLoad = layersToLoad.filter((d) => d.type !== "statuses");
      }

      //now we know what we need, set up the actual loading
      layersToLoad.forEach((d) => {
        //add it to the promiseChain for resolution
        promiseChain.push(
          this.config.layerTypes[d.type].pullFunc(d.instance_id)
        );

        //reset the pull loop timeout for each layer type. This is necessary because it may be being called with a different instace_id argument
        setLoopingTimeout(d.type, d.instance_id);
      });
    });

    return Promise.allSettled(promiseChain)
      .then(() => {
        this._mergeCampaignAggregatesWithLayers();
        this._mergeCrewInfoIntoStatuses("routesites");
        this._mergeCrewInfoIntoStatuses("turfs");
      })
      .catch((e) => console.error(e));
  };

  matchDataToLayers = (config = {}, appendData = false) => {
    /*
     * This function splits processes incoming data and inserts them into "layers" in the appropriate format
     *
     * config has: data (an array), a clusterType (canvasser, office, campaign), a layerType, "turf", "siteroutes" etc and an instance idKey (defaults to 'id')
     * the dataKey element is used to match the data object inside an instance.. which object from the dictionary should we set as the layers' data
     * the appendData argument will append data if there is an existing layer, rather than replace it.
     *
     * if we already have a layer for the data, update it. If we don't, create it.
     * If there are layers that don't have a matching id in the data, delete them.
     */

    let defaultConfig = {
      idKey: "id",
      data: [],
      dataKey: "data",
      nameKey: "name",
      layerType: "",
      clusterType: "",
      afterLayerCreateOrUpdate: null, // a passed callback that allows you to pass after effects after a layer has been loaded to the map
    };

    config = { ...defaultConfig, ...config };

    let relLayers = this.props.mapLayers.filter(
      (d) =>
        d.type === config.layerType && d.cluster.type === config.clusterType
    );

    //note here that data is an array of objects, and we expect to have one layer for each object.
    config.layers.forEach((d) => {
      let layer_id = this.getLayerId(
        config.clusterType,
        d.instance_id,
        config.layerType
      );

      let existingLayerIdx = relLayers.findIndex((l) => l.id === layer_id);

      if (existingLayerIdx >= 0) {
        let newData, newDisplayedData;
        let updateLayer = relLayers[existingLayerIdx];
        if (appendData) {
          this.props.updateMapLayer(layer_id, {
            data: [...updateLayer.data, ...d[config.dataKey]],
            displayedData: [
              ...updateLayer.displayedData,
              ...d[config.dataKey],
            ].map((d) => ({
              ...d,
              layer_id,
            })),
            updated: Date.now(),
          });
        } else if (config.mergeIfExists) {
          let data = updateLayer.displayedData
            .map((j) => {
              let match = d[config.dataKey].find((k) => j.key === k.key);
              //i've intentionally turned off merging style because it was breaking statuses, which is where we use this.
              return match ? { ...j, ...match, style: j.style } : null;
            })
            .filter((j) => j);
          this.props.updateMapLayer(layer_id, {
            data,
            displayedData: data,
            updated: Date.now(),
          });
        } else {
          this.props.updateMapLayer(layer_id, {
            data: d[config.dataKey],
            displayedData: d[config.dataKey],
            updated: Date.now(),
          });
        }
        relLayers.splice(existingLayerIdx, 1);

        if (config.afterLayerCreateOrUpdate) {
          let updatedLayer = this.props.mapLayers.find(
            (d) => d.id === layer_id
          );
          config.afterLayerCreateOrUpdate(updatedLayer);
        }
      } else {
        let newLayer = {
          data: d[config.dataKey], //DISPLAYED DATA is what we use for processing and display of all layers. This is here only as an original record of data.
          displayedData: (d[config.dataKey] || []).map((d) => ({
            ...d,
            layer_id,
          })), //every item must have layer id so it can hand it back for interactions
          featureType: d.featureType || "points", // can be 'points', 'convex_hull', 'line_string'
          groupBy: d.groupBy, //only necessary if feature type is not "points"
          getGroupedFeatureStyle: d.getGroupedFeatureStyle, // again, only necessary in the case of a grouped feature. must be a function that is passed the first element of the group.
          name: config.layerType,
          type: config.layerType,
          id: layer_id,
          instance_id: d.instance_id,
          cluster: {
            id: this.getClusterId(config.clusterType, d.instance_id),
            type: config.clusterType,
            name: d.name,
            parent: d.clusterParent,
          },
          visible: true,
          updated: Date.now(),
          excludeFromFit:
            this.config.layerTypes[config.layerType].excludeFromFit || false, //don't fit the extent of the map to this layer
        };

        this.props.addMapLayer(newLayer);

        if (config.afterLayerCreateOrUpdate) {
          config.afterLayerCreateOrUpdate(newLayer);
        }
      }
    });
  };

  /*
   * CAMPAIGN AGGREGATE CALLS
   * This works on a seperate timer from the rest of the layers, and is merged in to the layers
   * on every pull, and every time the focused cluster is updated
   * */
  _updateCampaignAggregates = () => {
    let { date } = this.state;
    return updateCampaignAggregates(date, date).then(
      this._mergeCampaignAggregatesWithLayers
    );
  };

  //to get the aggregates to display nicely, and not have to re-merge on every rerender,
  // we insert the data directly into the data dictionary of the layers.
  _mergeCampaignAggregatesWithLayers = () => {
    /*
        aggregates have "campaign", "office", and "assignedSites" key. Match those with the map layers and merge.
         */

    //start by merging offices. There is only one offices layer
    let officeLayer = this.props.mapLayers.find(
      (layer) => layer.type === "offices"
    );
    if (officeLayer) {
      let officeAggs = get(this.props, "aggregates.offices", []);
      let officeDisplayedData = [
        ...get(officeLayer, "displayedData", []).sort((a, b) => a.id > b.id),
      ];

      officeAggs
        .sort((a, b) => a.id > b.id)
        .forEach((agg) => {
          officeDisplayedData = officeDisplayedData.map((d) =>
            d.id === agg.id ? { ...agg, ...d } : d
          );
        });

      this.props.updateMapLayer(officeLayer.id, {
        displayedData: officeDisplayedData,
      });
    }

    //all status layers effectively represent assignedSites and have the same keys in their data objects
    // merge those objects with canvasser agggregates.
    let statusLayers = this.props.mapLayers.filter(
      (layer) => layer.type === "statuses"
    );
    let canvasserAggs = get(this.props, "aggregates.canvassers", []);

    statusLayers.forEach((layer) => {
      let displayedData = (layer.displayedData || []).map((canvasser) => {
        let agg = canvasserAggs.find((d) => d.id === canvasser.id) || {};
        return {
          ...agg,
          ...canvasser,
        };
      });
      this.props.updateMapLayer(layer.id, { displayedData });
    });
  };

  //where the assignment layer is either assigned sites or assigned turfs
  _mergeCrewInfoIntoStatuses = (type = "routesites") => {
    let cluster_id = this.state.focusedCluster;
    let clusterInfo = this.splitClusterId(cluster_id);

    //find status layer we need to update;
    let matchingStatusLayerId = this.getLayerId(
      clusterInfo.type,
      clusterInfo.instance_id,
      "statuses"
    );

    console.log(matchingStatusLayerId);

    let matchingStatusLayer = this.props.mapLayers.find(
      (d) => d.id === matchingStatusLayerId
    );

    let assignmentLayerId = this.getLayerId(
      clusterInfo.type,
      clusterInfo.instance_id,
      type
    );
    let assignmentLayer = this.props.mapLayers.find(
      (d) => d.id === assignmentLayerId
    );

    if (matchingStatusLayer && assignmentLayer) {
      let statuses = _.cloneDeep(matchingStatusLayer.displayedData);
      let assignments = assignmentLayer.displayedData;

      statuses.forEach((s) => {
        assignments.forEach((d) => {
          if (s.id === d.canvasser_id) {
            s.crew_id = d.crew_id;
            s.crew_name =
              d.siteroute_name || d.crew_name || "Crew " + d.crew_id;
            s.crewcanvasser_id = d.crewcanvasser_id;
            s.style = this.assignStatusStyle(s, {
              clusterType: matchingStatusLayer.cluster.type,
            });
          }
        });
        if (!s.crew_id) {
        }
      });

      statuses.sort((a, b) => {
        if (!a) a = 99999999999999;
        if (!b) b = 99999999999999;
        return a.crew_id > b.crew_id ? 1 : -1;
      });

      this.props.updateMapLayer(matchingStatusLayer.id, {
        displayedData: statuses,
      });
    } else {
    }
  };

  /*
   *
   * LAYER SPECIFIC PULL FUNCTIONS.
   *
   * EACH FUNCTION SHOULD TAKE A SINGLE INSTANCE_ID ARGUMENT
   * EACH FUNCTION MUST ALSO RETURN A PROMISE, SO WE CAN CHAIN ANY DATA POST PROCESSING, LIKE COLOR ASSINGMENT
   *
   * */

  pullOffices = () => {
    return pullCampaignOffices().then(() => {
      this.matchDataToLayers({
        clusterType: "campaign",
        layerType: "offices",
        layers: [
          //each object here is a layer
          {
            name: this.props.focusedCampaign.name,
            instance_id: this.props.focusedCampaign.id,
            data: this.props.offices.map((d) => ({
              ...d,
              latitude: +d.lat,
              longitude: +d.lon,
              style: this.config.layerTypes.offices.styleFunc(d),
            })),
          },
        ],
      });
    });
  };

  pullCanvasserStatuses = (office_id) => {
    // we blindly pass instance id to this func in matchDataLayersToFocusedCluster, so we have to check if we are in campaign or office
    //check if we are pulling archived or live. If we are pulling archived, don't update data all the time.

    let { type } = this.splitClusterId(this.state.focusedCluster);
    if (type === "campaign") office_id = 0;

    let offices_to_merge = office_id
      ? [office_id]
      : this.props.offices.map((d) => d.id);

    return pullCanvasserStatuses({
      office: office_id,
      campaign: this.props.focusedCampaign.id,
      date: this.state.date,
    }).then((statuses) => {
      let noGeoCanvassers = [];
      statuses &&
        statuses.forEach((d) => {
          let hasLocation = !d.last_locationupdate;
          if (
            hasLocation &&
            !this.state.noGeoCanvassers.map((c) => c.id).includes(d.id)
          ) {
            noGeoCanvassers = [...noGeoCanvassers, d];
          }
        });

      this.setState({ noGeoCanvassers });

      statuses = statuses.filter(
        (d) => !noGeoCanvassers.map((c) => c.id).includes(d.id)
      );

      //merge campaign layer if necessary
      if (office_id === 0) {
        this.matchDataToLayers({
          clusterType: "campaign",
          layerType: "statuses",
          mergeIfExists: true, //merge the new layer data into old data layer, instead of replacing. This prevents overwrite of crew data. Key must be provided in data to do this.
          layers: [
            //each object here is a layer
            {
              name: this.props.focusedCampaign.name,
              instance_id: this.props.focusedCampaign.id,
              data: statuses
                .map((d) => ({
                  ...d,
                  key: d.id,
                  office_name: (
                    this.props.offices.find((o) => o.id === +d.office_id) || {}
                  ).name,
                  style: this.assignStatusStyle(d, { clusterType: "campaign" }),
                }))
                .sort((a, b) => (a.office_id > b.office_id ? 1 : -1)),
            },
          ],
        });
      }

      console.log(offices_to_merge);
      //merge office layers
      this.matchDataToLayers({
        clusterType: "office",
        layerType: "statuses",
        mergeIfExists: true,
        layers: offices_to_merge.map((o_id) => ({
          name: (this.props.offices.find((d) => d.id === +o_id) || {}).name,
          instance_id: o_id,
          data: statuses
            .filter((d) => +d.office_id === +o_id)
            .map((d) => ({
              ...d,
              key: d.id,
              style: this.assignStatusStyle(d, { clusterType: "office" }),
            })),
          clusterParent: this.getClusterId(
            "campaign",
            this.props.focusedCampaign.id
          ),
        })),
      });

      //merge canvasser layers
      this.matchDataToLayers({
        clusterType: "canvasser",
        layerType: "statuses",
        mergeIfExists: true,
        layers: statuses.map((d, i) => ({
          name: statuses[i].firstname + " " + statuses[i].lastname,
          instance_id: statuses[i].id,
          data: [
            {
              ...statuses[i],
              key: statuses[i].id,
              style: this.assignStatusStyle(d, { clusterType: "canvasser" }),
            },
          ],
          clusterParent: this.getClusterId("office", d.office_id),
        })),
      });
    });
  };

  // TODO for both of these  -- FIGURE OUT IF WE;RE PULLING LIVE OR OLD DATA. IF OLD, DON'T SET PULL LOOP. IF NEW, SET PULL LOOP AND PASS MAX_ID, APPEND
  pullCanvasserEvents = (canvasser_id) => {
    return pullCanvasserEvents({
      canvasser: canvasser_id,
      date: this.state.date,
      details: true,
    })
      .then((events) => {
        if (!events.length) return;

        this.matchDataToLayers({
          clusterType: "canvasser",
          layerType: "events",
          layers: [
            {
              name:
                events[0].canvasser.firstname +
                " " +
                events[0].canvasser.lastname,
              instance_id: canvasser_id,
              data: events.map((d) => ({
                ...d,
                style: this.assignEventStyle(d),
              })),
              clusterParent: this.getClusterId(
                "campaign",
                this.props.focusedCampaign.id
              ), // todo: wrong
            },
          ],
        });
      })
      .then(() => {
        this.setBounds();
      });
  };

  pullCanvasserBreadcrumbs = (canvasser_id) => {
    pullCanvasserBreadcrumbs({
      canvasser: canvasser_id,
      date: this.state.date,
    }).then((data) => {
      this.matchDataToLayers({
        clusterType: "canvasser",
        layerType: "breadcrumbs",
        layers: [
          {
            name: "Breadcrumbs", //fixed this naming issue
            instance_id: canvasser_id,
            featureType: "line_string",
            groupBy: "segments",
            getGroupedFeatureStyle: (segmentIndex) => {
              let color = rgb(
                interpolatePuOr(segmentIndex / (data.length - 1))
              );
              let stroke = [color["r"], color["g"], color["b"], 0.8];
              return defaultLineStringStyle({ stroke });
            },
            data: data.map((d, i) => ({
              ...d,
              breadcrumb_count: data.length,
              breadcrumb_index: i,
            })), // need length and index so we can recreate the point color in the sidebar and on hover
            clusterParent: this.getClusterId(
              "campaign",
              this.props.focusedCampaign.id
            ), //todo: wrong
          },
        ],
      });
    });
  };

  pullAssignedSites = (office_id) => {
    pullAssignedSites({ office: office_id, date: this.state.date }).then(
      (data) => {
        if (!data) return;

        this.matchDataToLayers({
          clusterType: "office",
          layerType: "routesites",
          // afterLayerCreateOrUpdate: (args) => setTimeout(() => this._mergeCrewInfoIntoStatuses(args), 400),
          layers: [
            {
              name: (this.props.offices.find((d) => d.id === office_id) || {})
                .name,
              instance_id: office_id,
              data: data.map((d) => ({
                ...d,
                style: this.assignSiteStyle(d, { clusterType: "offices" }),
              })),
              clusterParent: this.getClusterId(
                "campaign",
                this.props.focusedCampaign.id
              ),
            },
          ],
        });

        //get distinct canvassers
        let canvasser_ids = [...new Set(data.map((d) => d.canvasser_id))];

        canvasser_ids.forEach((canvasser_id) => {
          this.matchDataToLayers({
            clusterType: "canvasser",
            layerType: "routesites",
            afterLayerCreateOrUpdate: (args) =>
              setTimeout(
                () => this._mergeCrewInfoIntoStatuses("routesites"),
                100
              ),
            layers: [
              {
                name: "Assigned Sites",
                instance_id: canvasser_id,
                data: data.map((d) => ({
                  ...d,
                  style: this.assignSiteStyle(d, { clusterType: "canvassers" }),
                })),
                clusterParent: this.getClusterId("office", office_id),
              },
            ],
          });
        });
      }
    );
  };

  pullAssignedTurfs = (office_id) => {
    pullAssignedTurfs({ office: office_id, date: this.state.date }).then(
      (data) => {
        // this needs to update when we show the shape on mapbox.
        // for now, we will map out each door

        if (!data) return;
        console.log(data);

        let flattened_data = [];
        (data || []).forEach((d) => {
          d.targethouseholds.forEach((h) => {
            flattened_data.push({ ...d, ...h, targethouseholds: null });
          });
        });

        flattened_data = _.uniqBy(flattened_data, (d) => d.id);

        this.matchDataToLayers({
          clusterType: "office",
          layerType: "turfs",
          afterLayerCreateOrUpdate: (args) =>
            setTimeout(() => this._mergeCrewInfoIntoStatuses("turfs"), 100),
          layers: [
            {
              name: (this.props.offices.find((d) => d.id === office_id) || {})
                .name,
              instance_id: office_id,
              featureType: "convex_hull",
              groupBy: "turf_name",
              data: data,
              getGroupedFeatureStyle: (first_of_group) =>
                this.assignTurfStyle(first_of_group, {
                  featureType: "convex_hull",
                  clusterType: "office",
                }),
              clusterParent: this.getClusterId(
                "campaign",
                this.props.focusedCampaign.id
              ),
              dataAsReceived: data,
            },
          ],
        });

        let canvasser_ids = [
          ...new Set(flattened_data.map((d) => d.canvasser_id)),
        ];

        canvasser_ids.forEach((canvasser_id) => {
          this.matchDataToLayers({
            clusterType: "canvasser",
            layerType: "turfs",
            featureType: "convex_hull",
            layers: [
              {
                name: "Turfs",
                instance_id: canvasser_id,
                // data: _.uniqBy(
                //   flattened_data.filter((d) => d.canvasser_id === canvasser_id),
                //   (d) => d.id
                // ).map((d) => ({
                //   ...d,
                //   style: this.assignTurfStyle(d),
                // })),
                data: data.filter((d) => d.canvasser_id === canvasser_id),
                clusterParent: this.getClusterId("office", office_id),
              },
            ],
          });
        });
      }
    );
  };

  /*
   *
   *  COLOR ASSIGNMENT FUNCTIONS
   *  colors relate by cluster
   * */

  getColorFromQualitativeScheme = (idx) => {
    const qualitative = [
      "#a6cee3",
      "#33a02c",
      "#b2df8a",
      "#1f78b4",
      "#6a3d9a",
      "#fb9a99",
      "#e31a1c",
      "#fdbf6f",
      "#ff7f00",
      "#cab2d6",
      "#b15928",
    ];

    return qualitative[idx % qualitative.length];
  };

  /*
   *  STYLE ASSIGNMENT FUNCTIONS, ONE PER LAYER TYPE
   *   ALL TAKE THE FOLLOWING ARGUMENTS:
   *
   *  featureData -- which is the data element from the "displayedData"array of the layer
   *   focused -- boolean of whether or not the point is focused
   *   config -- dict containing layer specific variables. This includes things like whether
   *             or not we need to return a polygon style or a point style for a turf household
   *             (i.e. whether the layer is grouped or not).
   *
   *             The config layer also contained the "focused" variable -- which is used to change styles on hover
   *
   * */

  encodeRouteSiteData = (featureData, config = {}) => {
    const getProperties = (routeSite) => {
      return {
        iconColor: this.getSiteColor(routeSite),
        iconName: "star",
        iconSize: 0.6,
      };
    };

    return this.encodePoints(featureData.displayedData, getProperties);
  };

  getRouteSiteSrcConf = (featureData, config = {}) => {
    return { style: { ...defaultSymbolStyle, id: `${featureData.id}-style` } };
  };

  encodeBreadcrumbData = (featureData) => {
    const getProperties = (breadcrumb) => {
      return {};
    };

    return this.encodeLine(featureData, getProperties);
  };

  getBreadcrumbSrcConf = (featureData, config = {}) => {
    return {
      style: { ...gradientLineStyle, id: `${featureData.id}-style` },
      lineMetrics: true,
    };
  };

  encodeEventData = (featureData) => {
    const getProperties = (event) => {
      return { groupColor: event.color };
    };

    return this.encodePoints(featureData.displayedData, getProperties);
  };

  getEventSrcConf = (featureData, config = {}) => {
    return {
      style: { ...groupedPointStyle, id: `${featureData.id}-style` },
    };
  };

  getTurfSrcConf = (featureData, config = {}) => {
    const style =
      featureData.cluster.type === "canvasser"
        ? defaultSymbolStyle
        : defaultPolyStyle;
    return {
      style: { ...style, id: `${featureData.cluster.id}-style` },
      outline: true,
    };
  };

  encodeTurfData = (featureData) => {
    const getProperties = (turf) => {
      return {
        polyColor: this.getTurfColor(turf, featureData.cluster.type),
        lineColor: this.getTurfColor(turf, featureData.cluster.type),
        iconColor: this.getTurfColor(turf, featureData.cluster.type),
        iconName: "homeFilled",
        iconSize: 0.4,
      };
    };

    if (featureData.cluster.type === "canvasser") {
      return this.encodePoints(
        featureData.displayedData[0].targethouseholds,
        getProperties
      );
    }

    return this.encodePolys(featureData.displayedData, getProperties);
  };

  getStatusSrcConfig = (featureData, config = {}) => {
    return {
      style: { ...groupedPointStyle, id: `${featureData.id}-style` },
    };
  };

  encodeStatusData = (featureData) => {
    const getProperties = (status) => {
      return {
        groupColor: this.getStatusColor(status, featureData.cluster.type),
        label: `${status.firstname} ${status.lastname}`,
      };
    };

    return this.encodePoints(featureData.displayedData, getProperties);
  };

  getOfficeSrcConfig = (featureData, config = {}) => {
    return {
      style: { ...defaultSymbolStyle, id: `${featureData.id}-style` },
    };
  };

  getOfficeColor = (featureData, clusterType) => {
    if (clusterType === "campaign") {
      return this.getColorFromQualitativeScheme(featureData.id);
    } else {
      return "#decb14";
    }
  };

  encodeOfficeData = (featureData) => {
    const getProperties = (office) => {
      return {
        iconColor: this.getOfficeColor(featureData.id),
        iconName: "star",
        iconSize: 1.2,
      };
    };
    return this.encodePoints(featureData.displayedData, getProperties);
  };

  assignStatusStyle = (featureData, config = {}) => {
    if (!config.clusterType)
      config.clusterType = this.splitClusterId(this.state.focusedCluster).type;
    let radius = config.clusterType === "canvasser" ? 10 : 6;
    if (config.focused) radius = radius * 2;

    return olStyles.centroidStyle({
      radius: radius,
      fill: this.getStatusColor(featureData, config.clusterType),
      name: featureData.lastname,
      showLabel: !config.focused,
      opacity: config.focused ? 1 : 0.8,
      zIndex: 100,
      stroke: "white",
    });
  };

  getStatusColor = (featureData, clusterType) => {
    switch (clusterType) {
      case "campaign":
        return this.getColorFromQualitativeScheme(featureData.office_id);
      case "canvasser":
        return "red";
      case "office":
        return this.getColorFromQualitativeScheme(
          featureData.siteroute_id || featureData.crew_id || 5
        );
      default:
        return this.getColorFromQualitativeScheme(featureData.id);
    }
  };

  getNoGeoWarningText = () => {
    let names = "";
    const { type: clusterType, instance_id } = this.splitClusterId(
      this.state.focusedCluster
    ).instance_id;

    let noGeoCanvassers =
      clusterType === "office"
        ? this.state.noGeoCanvassers.filter((d) => d.office_id === instance_id)
        : this.state.noGeoCanvassers;

    noGeoCanvassers.forEach((c) => {
      names += `${c.firstname} ${c.lastname}, `;
    });
    return `${names} have GPS turned off and are not displayed.`;
  };

  assignBreadcrumbPointStyle = (feature, config) => {
    return olStyles.crossStyle({});
  };

  getBreadcrumbColor = (feature, config = {}) => {
    let count = feature.breadcrumb_count || 1;
    let index = feature.breadcrumb_index || 1;
    let color = rgb(interpolatePuOr(index / count)); //return rgb string
    return color;
  };

  assignTurfStyle = (featureData, config = {}) => {
    if (!config.clusterType)
      config.clusterType = this.splitLayerId(featureData.layer_id).clusterType;
    let fill;
    switch (config.clusterType) {
      case "office":
        fill = this.getColorFromQualitativeScheme(featureData.crew_id);
        break;

      case "canvasser":
      default:
        fill = this.getColorFromQualitativeScheme(featureData.turf_id);
        break;
    }

    if (config.featureType === "convex_hull") {
      return coloredPolygonStyle(fill, 3, config.opacity || 0.8);
    } else {
      return genericSquareStyle({ fill });
    }
  };

  getTurfColor = (featureData, clusterType) => {
    switch (clusterType) {
      case "office":
        return this.getColorFromQualitativeScheme(featureData.crew_id);
      case "canvasser":
      default:
        // doors at canvasser level should all look the same regardless for contrast purposes
        return "#ff7f00";
    }
  };

  assignSiteStyle = (featureData, config) => {
    if (!config.clusterType)
      config.clusterType = this.splitLayerId(featureData.layer_id).clusterType;

    let fill;
    switch (config.clusterType) {
      case "canvasser":
        fill = this.getColorFromQualitativeScheme(featureData.site_id);
        break;

      case "office":
      default:
        fill = featureData.siteroute_hex_color || "#1f78b4";
        break;
    }

    return olStyles.starStyle({
      color: this.getSiteColor(featureData, config.clusterType),
      radius: config.focused ? 10 : 5,
    });
  };

  getSiteColor = (featureData, clusterType) => {
    return (
      featureData.siteroute_hex_color ||
      this.getColorFromQualitativeScheme(featureData.siteroute_id)
    );
  };

  assignEventStyle = (featureData, config = {}) => {
    return genericCircleStyle({
      radius: config.focused ? 10 : 4,
      fill: this.getEventColor(featureData),
      opacity: config.focused ? 1 : 0.7,
    });
  };

  getEventColor = (featureData) => {
    return getEventColorStyle(featureData.event_type);
  };

  _generateSiteRouteColorDict = (layerData) => {
    const colors = {};
    const availCount = this.markerColorArray.length;
    const assigned_ids = [];
    layerData.forEach((d, i) => {
      if (i >= availCount) i = i % availCount;
      if (!assigned_ids.includes(d.siteroute_id)) {
        colors[d.siteroute_id] = d.siteroute_color || this.markerColorArray[i];
        assigned_ids.push(d.siteroute_id);
      }
    });
    this.siteRouteColorDict = colors;
  };

  _generateRouteSeatColorDict = (layerData) => {
    const colors = {};
    const availCount = this.markerColorArray.length;
    const assigned_ids = [];
    layerData.forEach((d) => {
      let asl = assigned_ids.length;
      if (asl >= availCount) asl = asl % availCount;
      if (!assigned_ids.includes(d.routeseat_id)) {
        colors[d.routeseat_id] = this.markerColorArray[asl];
        assigned_ids.push(d.routeseat_id);
      }
    });
    this.routeSeatColorDict = colors;
  };

  _applyDataFilters(data) {
    this.state.appliedFilters.forEach((d) => {
      data = data.filter(dataFilters[d].func);
    });
    return [...data];
  }

  _generateSideBarClickFunction(layer_id, data) {
    let split_token = layer_id.split("-");
    let cluster_type = split_token[0],
      instance_id = split_token[1],
      layer_type = split_token[2];
    let func;
    switch (layer_type) {
      case "offices":
        func = () => this.updateCluster(`office-${data.id}`);
        break;

      case "statuses":
        func = () => this.updateCluster(`canvasser-${data.id}`);
        break;

      default:
        func = () => null;
    }

    return func;
  }

  // this function determines which layer should actually be listed in the layer
  _getSideBarDisplayedLayer() {
    let layers = this.props.mapLayers.filter(
      (d) => d.cluster.id === this.state.focusedCluster
    );
    if (!layers.length) return null;
    // let target_id;

    /*
        The focused layer can't just be there - we have to make sure it is from the same cluster!
        Otherwise, map to default for each cluster type set in Data Map
        Useful to remember that if id is null, splitClusterId and splitLayerId return an empty dict
        */
    // let { type: clusterType, instance_id: clusterInstanceId } = this.splitClusterId(
    //   this.state.focusedCluster
    // );
    // let { type: layerType, instance_id: layerInstanceId } = this.splitLayerId(
    //   this.state.sideBarLayerFocused
    // );

    // if (
    //   this.state.sideBarLayerFocused &&
    //   layerType === clusterType &&
    //   layerInstanceId === clusterInstanceId
    // ) {
    //   target_id = this.state.sideBarLayerFocused;
    // } else {
    //   target_id = this.getLayerId(
    //     clusterType,
    //     clusterInstanceId,
    //     this.config.clusterTypes[clusterType].defaultDisplayedLayerType
    //   );
    // }
    return layers.find((layer) => layer.id === this.state.sideBarLayerFocused);
  }

  /// this simply renders the topline aggregates display that headlines each cluster sidebar
  _renderAggregateMetricsDisplay(layer) {
    let aggs, fields;
    const { aggregates } = this.props;

    switch (layer.cluster.type) {
      case "campaign":
        try {
          aggs = aggregates.campaign[0] || {};
        } catch {
          aggs = {};
        }
        fields = [
          "num_response",
          "num_checkins",
          "num_site_contact_attempts",
          "num_sites_checked_into",
          "canvasser_count",
          "door_targets_canvassed",
          "households_canvassed",
        ];
        break;

      case "office":
        let office_id = layer.instance_id;
        aggs = aggregates.offices.find((d) => d.id === layer.instance_id) || {};
        fields = [
          "num_response",
          "num_checkins",
          "num_sites_checked_into",
          "num_site_contact_attempts",
          "canvasser_count",
          "door_targets_canvassed",
          "households_canvassed",
        ];

        break;

      case "canvasser":
        aggs =
          aggregates.canvassers.find((d) => d.id === layer.instance_id) || {};
        fields = [
          "num_response",
          "num_checkins",
          "num_sites_checked_into",
          "num_site_contact_attempts",
          "door_targets_canvassed",
          "households_canvassed",
        ];
        break;

      default:
        aggs = {};
        break;
    }

    aggs = Object.keys(aggs).reduce((acc, d, i, a) => {
      if (fields.includes(d)) {
        return { ...acc, [d]: aggs[d] };
      } else {
        return acc;
      }
    }, {});

    const displayElems = Object.keys(aggs).reduce((acc, d, i, a) => {
      let elem;
      if (d === "canvasser_count") {
        elem = (
          <>
            {!this.state.dataMode === "archive" && (
              <div className="agg-metric">
                <span className="metricKey">Responding Canvassers</span>
                <span className="metricValue">{aggs["canvasser_count"]}</span>
              </div>
            )}
            {layer.name === "canvassers" && (
              <div className="agg-metric">
                <span className="metricKey">
                  {!this.state.dataMode === "archive"
                    ? "Logged In"
                    : "Responding"}{" "}
                  Canvassers
                </span>
                <span className="metricValue">
                  {_.uniqBy(layer.displayedData, "id").length}
                </span>
              </div>
            )}
          </>
        );
      } else {
        elem = (
          <div className="agg-metric">
            <span className="metricKey">{startCase(d)}</span>
            <span className="metricValue">{aggs[d]}</span>
          </div>
        );
      }

      return [...acc, elem];
    }, []);
    return displayElems;
  }

  buildOfficeSideBar = (layer) => {
    let config = this.config.layerTypes.offices;
    let ListComponent = config.listComponent;

    return layer.displayedData.map((d) => {
      // insert aggregate data into the list
      // insert aggregate data into the list
      d.id = +d.id;

      return (
        <ListComponent
          id={generateId(d.id)}
          focused={this.state.focusedMarker === d.id}
          onMouseEnter={() =>
            this._setFocusedMarker(d.id, layer.id, { disableScroll: true })
          }
          onMouseLeave={() => {
            if (this.state.focusedMarker === d.id) {
              // in case of race-condition from mouse movements
              this._setFocusedMarker(null, layer.id, { disableScroll: true });
            }
          }}
          onTitleClick={() => this.updateCluster(`office-${d.id}`)}
          data={d}
          metaData={layer.metaData}
          key={generateId(d.id)}
        />
      );
    });
  };

  buildStatusSideBar = (layer) => {
    let config = this.config.layerTypes.statuses;
    let ListComponent = config.listComponent;

    let groupedBy = null;
    let getGroupTitle = () => null;

    if (layer.cluster.type === "campaign") {
      groupedBy = "office_id";
      getGroupTitle = (d) => d.office_name;
    } else if (layer.cluster.type === "office") {
      groupedBy = "crew_id";
      getGroupTitle = (d) => d.crew_name;
    }

    let componentArr = [];

    _.sortBy(
      layer.displayedData.map((d) => ({ ...d, groupTitle: getGroupTitle(d) })),
      "groupTitle"
    ).forEach((d, i, a) => {
      // insert aggregate data into the list
      // insert aggregate data into the list
      d.id = +d.id;
      d.color = config.colorFunc(d, layer.cluster.type);

      //check if layer is different than before, insert header if needed
      //two conditions: one for if is greater than zero, the other if i is zero
      if (
        (i === 0 && groupedBy) ||
        (i > 0 && groupedBy && d[groupedBy] !== a[i - 1][groupedBy])
      ) {
        componentArr.push(
          <div className={"side-menu-list-divider"}>
            <h5>{getGroupTitle(d)}</h5>
          </div>
        );
      }

      // need to make sure that if a canvasser does not have any valid
      // geolocational points, that the campaign manager knows

      d.bad_loc_on_last_update = !d.last_locationupdate;

      componentArr.push(
        <ListComponent
          id={generateId(d.id)}
          focused={this.state.focusedMarker === d.id}
          onMouseEnter={() =>
            this._setFocusedMarker(d.id, layer.id, { disableScroll: true })
          }
          onMouseLeave={() => {
            if (this.state.focusedMarker === d.id) {
              this._setFocusedMarker(null, layer.id, { disableScroll: true });
            }
          }}
          onTitleClick={() => this.updateCluster(`canvasser-${d.id}`)}
          data={d}
          metaData={layer.metaData}
          key={generateId(d.id)}
        />
      );
    });

    return componentArr;
  };

  buildSiteSideBar = (layer) => {
    let config = this.config.layerTypes.routesites;
    let ListComponent = config.listComponent;
    let data = layer.displayedData;

    let groupedBy = "siteroute_id";
    let getGroupTitle = (d) => d.siteroute_name;

    let componentArr = [];

    data = _.sortBy(data, "siteroute_name");

    data.forEach((d, i, a) => {
      // insert aggregate data into the list
      // insert aggregate data into the list
      d.id = +d.id;
      d.color = config.colorFunc(d, layer.cluster.type);

      //check if layer is different than before, insert header if needed
      //two conditions: one for if is greater than zero, the other if i is zero
      if (
        (i === 0 && groupedBy) ||
        (i > 0 && groupedBy && d[groupedBy] !== a[i - 1][groupedBy])
      ) {
        componentArr.push(
          <div className={"side-menu-list-divider"}>
            <h5>{getGroupTitle(d)}</h5>
          </div>
        );
      }

      componentArr.push(
        <ListComponent
          id={generateId(d.id)}
          focused={this.state.focusedMarker === d.id}
          onMouseEnter={() =>
            this._setFocusedMarker(d.id, layer.id, { disableScroll: true })
          }
          onMouseLeave={() => {
            if (this.state.focusedMarker === d.id) {
              this._setFocusedMarker(null, layer.id, { disableScroll: true });
            }
          }}
          onTitleClick={() => null}
          data={d}
          metaData={layer.metaData}
          key={generateId(d.id)}
          hideAssignment={layer.cluster.type === "canvasser"}
        />
      );
    });

    return componentArr;
  };

  buildTurfSideBar = (layer) => {
    let config = this.config.layerTypes.turfs;
    let ListComponent = config.listComponent;
    let data = layer.displayedData;

    let groupedBy = "crew_id";

    let componentArr = [];

    //take only the first door
    data = _.uniqBy(data, (d) => d.turf_id).map((d) => ({
      ...d,
      name: d.turf_name,
    }));
    data = _.sortBy(data, "turf_name");

    data.forEach((d, i, a) => {
      // insert aggregate data into the list
      d.id = +d.turf_id;
      d.door_count = layer.data.length;
      d.color = config.colorFunc(d, layer.cluster.type);

      //check if layer is different than before, insert header if needed
      //two conditions: one for if is greater than zero, the other if i is zero
      if (
        (i === 0 && groupedBy) ||
        (i > 0 && groupedBy && d[groupedBy] !== a[i - 1][groupedBy])
      ) {
        componentArr.push(
          <div className={"side-menu-list-divider"}>
            <h5>Crew {d.crew_name}</h5>
          </div>
        );
      }
      componentArr.push(
        <ListComponent
          id={generateId(d.id)}
          focused={this.state.focusedMarker === d.id}
          onMouseEnter={() =>
            this._setFocusedMarker(d.id, layer.id, { disableScroll: true })
          }
          onMouseLeave={() => {
            if (this.state.focusedMarker === d.id) {
              this._setFocusedMarker(null, layer.id, { disableScroll: true });
            }
          }}
          onTitleClick={() => null}
          data={d}
          metaData={layer.metaData}
          key={generateId(d.id)}
          hideAssignment={layer.cluster.type === "canvasser"}
        />
      );
    });

    return componentArr;
  };

  buildBreadcrumbSideBar = (layer) => {
    let config = this.config.layerTypes.breadcrumbs;
    let ListComponent = config.listComponent;
    let data = layer.displayedData;

    let componentArr = [];

    data.forEach((d, i, a) => {
      // insert aggregate data into the list
      // insert aggregate data into the list
      d.id = +d.id;
      d.color = config.colorFunc(d, layer.cluster.type);

      let gap_to_next = 0;
      if (i < a.length - 1) {
        gap_to_next = Moment(a[i + 1].time).diff(Moment(d.time), "minutes");
      }
      componentArr.push(
        <ListComponent
          id={generateId(d.id)}
          focused={this.state.focusedMarker === d.id}
          onMouseEnter={() =>
            this._setFocusedMarker(d.id, layer.id + "-sub-elements", {
              disableScroll: true,
            })
          }
          onMouseLeave={() => {
            if (this.state.focusedMarker === d.id) {
              this._setFocusedMarker(null, layer.id, { disableScroll: true });
            }
          }}
          onTitleClick={() => null}
          data={d}
          metaData={layer.metaData}
          key={generateId(d.id)}
        />
      );
      if (gap_to_next > 15) {
        componentArr.push(
          <div
            className={"gap-warning-sidebar-wrapper"}
            key={`gapmarker-${d.id}`}
          >
            <div className={"gap-warning-line"}>{gap_to_next} Minute Gap</div>
          </div>
        );
      }
    });

    return componentArr;
  };

  buildEventSideBar = (layer) => {
    let config = this.config.layerTypes.events;
    let ListComponent = config.listComponent;
    let data = layer.displayedData;

    let componentArr = [];

    data.forEach((d, i, a) => {
      // insert aggregate data into the list
      // insert aggregate data into the list
      d.id = +d.id;
      d.color = config.colorFunc(d, layer.cluster.type);

      let gap_to_next = 0;
      if (i < a.length - 1) {
        gap_to_next = Moment(a[i + 1].phone_time).diff(
          Moment(d.phone_time),
          "minutes"
        );
      }

      componentArr.push(
        <ListComponent
          id={generateId(d.id)}
          focused={this.state.focusedMarker === d.id}
          onMouseEnter={() =>
            this._setFocusedMarker(d.id, layer.id, { disableScroll: true })
          }
          onMouseLeave={() => {
            if (this.state.focusedMarker === d.id) {
              this._setFocusedMarker(null, layer.id, { disableScroll: true });
            }
          }}
          onTitleClick={() => null}
          data={d}
          metaData={layer.metaData}
          key={generateId(d.id)}
        />
      );

      if (gap_to_next > 15) {
        componentArr.push(
          <div className={"gap-warning-sidebar-wrapper"}>
            <div className={"gap-warning-line"}>{gap_to_next} Minute Gap</div>
          </div>
        );
      }
    });

    //as a last thing, we push the timeline into the component Array. This is actually floating over the map! But this is the best place to insert it.

    let timeline = (
      <>
        <div className={"timeline-floating"}>
          <CanvasserEventsTimeline
            data={_.sortBy(layer.displayedData, "phone_time")}
            onEventBoxMouseEnter={(d) => {
              this._setFocusedMarker(d.id, layer.id, { disableScroll: true });
            }}
            onEventBoxMouseLeave={(d) =>
              this._setFocusedMarker(null, layer.id, { disableScroll: true })
            }
          />
        </div>
      </>
    );

    componentArr.push(timeline);

    return componentArr;
  };

  _renderSideBarClusterDisplay() {
    //mostly for loading. If we haven't decided which layer to show yet, don't try and render.
    if (!this.state.focusedCluster) return <div />;

    let clusterInfo = this.splitClusterId(this.state.focusedCluster);

    let layers = this.props.mapLayers.filter(
      (d) => d.cluster.id === this.state.focusedCluster
    );
    let displayedLayer = this._getSideBarDisplayedLayer() || { cluster: {} };

    return (
      <div>
        <div className="side-menu-title">
          <h2>
            {this.config.clusterTypes[clusterInfo.type].getClusterName(
              this.state.focusedCluster
            )}
          </h2>
          <h5>{clusterInfo.type}</h5>
        </div>
        <div className="daterange-wrapper">
          <DatePicker // updates with and returns a native js date obj
            selected={Moment(this.state.date, this.SUBMIT_DATE_FORMAT).toDate()}
            onChange={(date) => this.updateDate(date)}
            minDate={new Date(2020, 0, 20)}
            maxDate={new Date()}
          />
        </div>
        <div className="agg-metrics">
          {this._renderAggregateMetricsDisplay(displayedLayer)}
        </div>
        {layers.length > 1 && (
          <div className="layer-filter-options">
            {this._renderSideBarClusterLayerOptions()}
          </div>
        )}

        <div className="side-menu-body">
          {this._renderSideBarDataList(displayedLayer)}
        </div>
      </div>
    );
  }

  _isError = () => {
    // return this.state.noGeoCanvassers.length > 0;
    return false;
  };

  _renderSideBarDataList(layer) {
    if (!layer.id) return; //prevent empty layer loads

    let config = this.config.layerTypes[layer.type];

    let ListComponent = config.listComponent;
    // filter out any matching ids so they don't pollute the sidebar
    let data = _.uniqBy(layer.displayedData, "id");

    if (!data || !data.length) {
      //nothing to show
      return <NoDataListItem />;
    }

    if (config.sideBarDisplayFunc) return config.sideBarDisplayFunc(layer);

    if (layer.type === "turfs") {
      //change this later, hack so we don't show one item for each household

      data = _.uniqBy(data, (d) => d.turf_id).map((d) => ({
        ...d,
        name: d.turf_name,
      }));
    }

    return data.map((d) => {
      // insert aggregate data into the list
      d.id = +d.id;

      return (
        <ListComponent
          id={generateId(d.id)}
          focused={this.state.focusedMarker === d.id}
          onMouseEnter={() =>
            this._setFocusedMarker(d.id, layer.id, { disableScroll: true })
          }
          onMouseLeave={() => {
            if (this.state.focusedMarker === d.id) {
              this._setFocusedMarker(null, layer.id, { disableScroll: true });
            }
          }}
          onTitleClick={this._generateSideBarClickFunction(layer.id, d)}
          data={d}
          metaData={layer.metaData}
          key={generateId(d.id)}
          showTimes={this.state.dataMode === "live"}
        />
      );
    });
  }

  _scrollToSideBarElement(elem) {
    Scroll.scroller.scrollTo("elem-" + elem.id, {
      duration: 300,
      delay: 50,
      smooth: true,
      containerId: "side-menu-scroll-wrapper",
      offset: -100, // Scrolls to element + 50 pixels down the page
    });
  }

  _renderSideBarClusterLayerOptions() {
    let handleLayerToggle = (id) => {
      const isVisibleNow = this.props.mapLayers.find(
        (d) => d.id === id
      ).visible;
      this.props.updateMapLayer(id, { visible: !isVisibleNow });
      this.setState({ refresh: this.state.refresh + 1 });
    };

    let layers = this.props.mapLayers
      .filter((d) => this.state.focusedCluster === d.cluster.id)
      .filter((d) => {
        let layersToDisplay = get(
          this.config,
          `clusterTypes[${d.cluster.type}].sideBarDisplayedLayers`,
          null
        );
        if (layersToDisplay) {
          return layersToDisplay.includes(d.type);
        } else {
          return true;
        }
      })
      .reduce((final, d) => {
        let layer = (
          <div key={d.id} className="filter-option-wrapper">
            <span
              className={`fa ${d.visible ? "fa-eye" : "fa-eye-slash"}`}
              onClick={() => handleLayerToggle(d.id)}
              name={d.layer_id}
            />
            <div
              className={`filter-option ${
                d.id === this.state.sideBarLayerFocused ? "focused" : ""
              }`}
              onClick={() => this.setState({ sideBarLayerFocused: d.id })}
            >
              {_.startCase(_.toLower(d.name))}
            </div>
          </div>
        );
        return [...final, layer];
      }, []);

    return layers;
  }

  _renderSideBarControlFilterOptions() {
    let handleFilterToggle = (event) => {
      const key = event.target.name;
      if (this.state.appliedFilters.indexOf(key) > -1) {
        this.setState({
          appliedFilters: [
            ...this.state.appliedFilters.filter((k) => k !== key),
          ],
        });
      } else {
        this.setState({ appliedFilters: [...this.state.appliedFilters, key] });
      }
      this.setState({ refresh: this.state.refresh + 1 });
    };

    let filters = Object.values(dataFilters)
      .filter((d) => d.showClientOption)
      .reduce((final, d) => {
        let filter = (
          <div className="filter-option">
            <input
              type="checkbox"
              checked={this.state.appliedFilters.indexOf(d.key) > -1}
              onChange={handleFilterToggle}
              name={d.key}
            />
            <label className="label-inline">{d.name}</label>
          </div>
        );
        return [...final, filter];
      }, []);

    return filters;
  }

  _setFocusedMarker(key, layer_id, options) {
    if (!options) options = {};
    console.log(
      `_setFocusedMarker key ${key} layer: ${layer_id}  options: ${JSON.stringify(
        options
      )}`
    );
    const { center, noState, disableScroll } = {
      center: true,
      noState: false,
      disableScroll: false,
      ...options,
    };
    //where we are using the db id passed from django as the key
    if (!noState) {
      this.setState({
        focusedMarker: key,
        focusedSource: key ? layer_id : null,
      });
    }
    // scroll item in sidebar into view
    if (key && document.getElementById(generateId(key)) && !disableScroll) {
      scrollIntoView(document.getElementById(generateId(key)), {
        behavior: "smooth",
        scrollMode: "if-needed",
      });
    }
  }

  getLayersForMap = () => {
    return this.props.mapLayers
      .filter(
        (d) =>
          d.cluster.id === this.state.focusedCluster || d.type === "offices"
      )
      .filter((d) => d.visible)
      .map((d) => ({ ...d, features: d.displayedData }));
  };

  encodeLine = (line, getProperties, source) => {
    if (!getProperties || typeof getProperties !== "function")
      getProperties = () => ({});

    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          id: +line.instance_id,
          geometry: {
            type: "LineString",
            coordinates: line.displayedData.map((p) => [
              p.longitude,
              p.latitude,
            ]),
          },
          properties: {
            data: line,
            disabled: false,
            ...getProperties(line),
          },
        },
      ],
    };
  };

  encodePolys = (polys, getProperties, source) => {
    if (!getProperties || typeof getProperties !== "function")
      getProperties = () => ({});

    return {
      type: "FeatureCollection",
      features: polys.map((poly) => {
        let encodedPoints = this.encodePoints(poly.targethouseholds);
        let convexHull = null;
        if (encodedPoints.features.length > 2) {
          convexHull = convex(encodedPoints);
        }
        if (convexHull) {
          convexHull = transformScale(convexHull, 1.1);
        }
        return {
          ...(convexHull || {}),
          id: poly.turf_id,
          properties: {
            data: poly,
            disabled: false,
            ...getProperties(poly),
          },
        };
      }),
    };
  };

  encodePoints = (points, getProperties, source) => {
    if (!getProperties || typeof getProperties !== "function")
      getProperties = () => ({});

    return {
      type: "FeatureCollection",
      features: points.map((point) => {
        return {
          type: "Feature",
          id: point.id,
          geometry: {
            type: "Point",
            coordinates: [
              +point.lon || +point.longitude || 0,
              +point.lat || +point.latitude || 0,
            ],
          },
          properties: {
            label: point.name || point.site_name,
            data: point,
            disabled: false,
            ...getProperties(point),
          },
        };
      }),
    };
  };

  getSources = (layers) => {
    let extraSources = [];
    const sources = layers.map((layer) => {
      let { layerType, clusterType, instance_id } = this.splitLayerId(layer.id);
      let srcConfigFunc =
        this.config.layerTypes[layerType]?.srcConfigFunc || ((l) => ({}));
      let encodeFunc =
        this.config.layerTypes[layerType]?.encodingFunc ||
        ((layer) => this.encodePoints(layer.data));
      let ListComponent = this.config.layerTypes[layerType].listComponent;
      const isInteractive = !(
        layerType === "breadcrumbs" ||
        (layerType === "turfs" && clusterType === "canvasser")
      );

      let layerConfig = {
        id: layer.id,
        type: "geojson",
        data: encodeFunc(layer),
        style: { ...defaultPointStyle, id: "defaultPointLayer" + layer.id },
        isInteractive: isInteractive,
        onHover: (feature) => {},
        onEndHover: (feature) => {},
        onDblClick: (feature) => {},
        popupComponentGenerator: (feature) => {
          return (
            <ListComponent
              id={generateId(feature.id)}
              focused={this.state.focusedMarker === feature.id}
              onMouseEnter={() =>
                this._setFocusedMarker(feature.id, layer.id, {
                  disableScroll: true,
                })
              }
              onMouseLeave={() => {
                if (this.state.focusedMarker === feature.id) {
                  this._setFocusedMarker(null, layer.id, {
                    disableScroll: true,
                  });
                }
              }}
              onTitleClick={() => this.updateCluster(layer.id)}
              data={feature.properties.data}
              metaData={layer.metaData}
              key={generateId(feature.id)}
            />
          );
        },
        ...srcConfigFunc(layer),
      };

      if (layerType === "breadcrumbs") {
        extraSources.push({
          ...layerConfig,
          id: layer.id + "-sub-elements",
          data: this.encodePoints(layer.displayedData, () => {}),
          style: { ...onlyHoverPointStyle, id: "sub-elements" },
        });
      }

      return layerConfig;
    });
    return [...sources, ...extraSources];
  };

  render() {
    const clusterInfo = this.splitClusterId(this.state.focusedCluster);

    return (
      <div>
        <FloatHeader />

        <div style={{ width: "100vw", height: "100vh" }}>
          {this.state.loaded && (
            <BaseMap
              requiredIcons={[
                { iconName: "star", iconSrc: "/icons/star.png" },
                { iconName: "homeFilled", iconSrc: "/icons/homeFilled.png" },
              ]}
              handleViewChange={(viewState, ref) => {
                this.setState({ viewState });
                if (ref) this.mapRef = ref;
              }}
              sources={this.getSources(this.getLayersForMap())}
              hoveredFeatures={[this.state.focusedMarker]}
              hoveredSource={this.state.focusedSource}
            />
          )}

          <div
            className={`map-side-menu ${this.state.sideBarClosed && "closed"}`}
          >
            <div className="side-menu-wrapper" id="side-menu-scroll-wrapper">
              <div className="side-menu-top-icons">
                <span
                  className="top-icon fa fa-level-up-alt"
                  onClick={this._goToCurrClusterParent}
                />
              </div>
              {this._renderSideBarClusterDisplay()}
            </div>
            <div
              className={`side-menu-select-wrapper ${
                this.state.sideBarClosed && "closed"
              }`}
            >
              <div
                className="side-menu-option toggle"
                style={{ marginTop: "12vh" }}
                onClick={() =>
                  this.setState({ sideBarClosed: !this.state.sideBarClosed })
                }
              >
                <i
                  className={`fas ${
                    this.state.sideBarClosed
                      ? "fa-arrow-left"
                      : "fa-arrow-right"
                  }`}
                />
              </div>
              {this.props.isLoading && (
                <div className={`side-menu-option loading`}>
                  <i className="fas fa-asterisk" />
                </div>
              )}
              {["campaign", "office"].includes(clusterInfo.type) && (
                <FeedbackTile
                  text={
                    <div style={{ fontSize: "11px" }}>
                      <i
                        className="fas fa-exclamation-triangle"
                        style={{ marginRight: "15px" }}
                      />
                      {this.getNoGeoWarningText()}
                    </div>
                  }
                  visible={this.state.noGeoCanvassers.length}
                  classNames="danger"
                />
              )}
            </div>
          </div>
          <ImageViewBox />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  mapLayers: state.trackerMap.mapLayers,
  isLoading:
    state.trackerMap.isLoading ||
    state.aggregates.loading ||
    state.reportCanvasserEvents.loading,
  focusedCampaign: state.session.focusedCampaign,
  aggregates: state.aggregates.data,
  error: state.trackerMap.error || state.aggregates.error,
  offices: state.offices.data,
});

const mapDispatchToProps = (dispatch) => ({
  addMapLayer: (layer) => dispatch(addMapLayer(layer)),
  updateMapLayer: (id, data) =>
    dispatch(updateMapLayer({ id, layerUpdates: { ...data } })),
  removeMapLayer: (cluster) => dispatch(removeMapLayer(cluster)),
  clearMapLayers: () => dispatch(clearMapLayers()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withCookies(TrackerMap));
